/**
 * keywordToLinkMapper.jsx — Production-Grade Internal Linking Utility
 *
 * Features:
 *  ✅ Regex word-boundary matching (no partial-word false positives)
 *  ✅ Alias / variation system per keyword (scalable, no hardcoding)
 *  ✅ First-occurrence-only linking (usedSet shared across paragraphs)
 *  ✅ Never links inside headings (caller skips h1/h2 blocks)
 *  ✅ Longest-match-first (phrases before individual words)
 *  ✅ Max-links-per-paragraph cap (no over-linking)
 *  ✅ Priority ordering: Blog → City → Itinerary
 *  ✅ useMemo-friendly (pure functions, stable output)
 */

import React from "react";
import { Link } from "react-router-dom";

/* ─────────────────────────────────────────────────
   TYPES
   keyword entry: { keyword, href, priority }
   priority: 1 = blog, 2 = city, 3 = itinerary
───────────────────────────────────────────────── */

/** Convert a URL slug → human-readable title ("new-york-city" → "New York City") */
export function slugToTitle(slug = "") {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Build keyword map from city slugs + optional itinerary slugs.
 *
 * Each city slug auto-generates multiple keyword variations:
 *   "Goa", "Goa trip", "trip to Goa", "Goa beaches", "Goa travel",
 *   "Goa itinerary", "Goa travel guide", "Goa plan"
 *
 * Priority order (for link injection):
 *   1 → blog links  (travel guide)
 *   2 → city links  (city name / variations)
 *   3 → itinerary links
 *
 * @param  {string[]} citySlugs       - e.g. ["goa", "bali"]
 * @param  {string[]} itinerarySlugs  - e.g. ["goa", "bali-adventure"]
 * @returns {Array<{keyword, href, priority}>}
 */
export function buildKeywordMap(citySlugs = [], itinerarySlugs = []) {
  const entries = [];

  citySlugs.forEach((slug) => {
    const name = slugToTitle(slug);

    /* ── Priority 1: Blog / travel guide ── */
    [
      `${name} travel guide`,
      `${name} guide`,
    ].forEach((kw) =>
      entries.push({ keyword: kw, href: `/blogs/${slug}-travel-guide`, priority: 1 })
    );

    /* ── Priority 2: City page ── */
    [
      name,                         // "Goa"
      `${name} trip`,               // "Goa trip"
      `trip to ${name}`,            // "trip to Goa"
      `visit ${name}`,              // "visit Goa"
      `${name} travel`,             // "Goa travel"
      `${name} tourism`,            // "Goa tourism"
      `things to do in ${name}`,    // "things to do in Goa"
      `explore ${name}`,            // "explore Goa"
    ].forEach((kw) =>
      entries.push({ keyword: kw, href: `/cities/${slug}`, priority: 2 })
    );

    /* ── Priority 3: Itinerary ── */
    [
      `${name} itinerary`,          // "Goa itinerary"
      `${name} plan`,               // "Goa plan"
      `${name} tour`,               // "Goa tour"
      `${name} days`,               // "Goa days"
    ].forEach((kw) =>
      entries.push({ keyword: kw, href: `/itineraries/${slug}`, priority: 3 })
    );
  });

  /* Extra itinerary slugs that don't match any city slug */
  itinerarySlugs.forEach((slug) => {
    if (citySlugs.includes(slug)) return; // already covered above
    const name = slugToTitle(slug);
    [
      `${name} itinerary`,
      `${name} plan`,
      `${name} tour`,
    ].forEach((kw) =>
      entries.push({ keyword: kw, href: `/itineraries/${slug}`, priority: 3 })
    );
  });

  /* Sort: priority ASC first (1 before 3), then length DESC (longer phrases win) */
  entries.sort((a, b) =>
    a.priority !== b.priority
      ? a.priority - b.priority
      : b.keyword.length - a.keyword.length
  );

  return entries;
}

/**
 * Build a case-insensitive, word-boundary–safe RegExp for a keyword.
 * Escapes special regex chars so keyword phrases work correctly.
 */
function buildRegex(keyword) {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<![\\w])${escaped}(?![\\w])`, "i");
}

/**
 * Inject internal <Link>s into a plain-text paragraph string.
 *
 * Rules:
 *  - Word-boundary regex (no partial-word matches)
 *  - First occurrence per keyword only (usedSet shared across call sites)
 *  - Max MAX_LINKS_PER_PARAGRAPH links per paragraph (default 2)
 *  - Priority order respected (blog > city > itinerary)
 *
 * @param {string} text                 - Plain paragraph text
 * @param {Array}  keywords             - Output of buildKeywordMap()
 * @param {Set}    usedSet              - Shared across paragraphs to prevent duplicates
 * @param {number} [maxLinks=2]         - Max links inserted per paragraph
 * @returns {Array}                     - React nodes (strings + <Link>s)
 */
export function injectInternalLinks(
  text,
  keywords,
  usedSet = new Set(),
  maxLinks = 2
) {
  if (!text || !keywords.length) return [text];

  let remaining = text;
  const nodes = [];
  let linksInserted = 0;
  let safety = 0;

  while (remaining.length > 0 && safety++ < 2000) {
    if (linksInserted >= maxLinks) {
      nodes.push(remaining);
      break;
    }

    let bestMatch = null; // { idx, keyword, href, length }

    for (const { keyword, href } of keywords) {
      const key = keyword.toLowerCase();
      if (usedSet.has(key)) continue;

      const regex = buildRegex(keyword);
      const match = regex.exec(remaining);
      if (!match) continue;

      const idx = match.index;
      /* Keep the earliest-occurring match (leftmost position wins) */
      if (!bestMatch || idx < bestMatch.idx) {
        bestMatch = { idx, keyword, href, length: match[0].length, originalText: match[0] };
      }
    }

    if (!bestMatch) {
      nodes.push(remaining);
      break;
    }

    const { idx, keyword, href, length, originalText } = bestMatch;

    if (idx > 0) nodes.push(remaining.slice(0, idx));

    nodes.push(
      <Link
        key={`${keyword}-${idx}-${linksInserted}`}
        to={href}
        className="text-[#5b7c67] font-medium underline underline-offset-2 decoration-[#5b7c67]/40 hover:text-[#4a6a56] hover:decoration-[#4a6a56] transition-colors"
      >
        {originalText}
      </Link>
    );

    usedSet.add(keyword.toLowerCase());
    remaining = remaining.slice(idx + length);
    linksInserted++;
  }

  return nodes;
}

/**
 * Process full blog content (split by double-newline) and inject links
 * only into paragraph blocks — headings and images are returned as-is.
 *
 * @param {Array}  blocks    - Output of parseContent() in BlogDetail
 * @param {Array}  keywords  - Output of buildKeywordMap()
 * @returns {Set}            - The shared usedSet (so callers can reuse it)
 */
export function createSharedUsedSet() {
  return new Set();
}
